<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { Line } from 'vue-chartjs';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import CaptainAnalyticsAPI from 'dashboard/api/captain/analytics';
import MetricCard from 'dashboard/routes/dashboard/captain/analytics/components/MetricCard.vue';
import Spinner from 'shared/components/Spinner.vue';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const { t } = useI18n();

const summary = ref(null);
const timeseries = ref([]);
const isLoading = ref(false);

const today = new Date().toISOString().split('T')[0];
const fifteenDaysAgo = new Date(Date.now() - 15 * 86400000)
  .toISOString()
  .split('T')[0];

const startDate = ref(fifteenDaysAgo);
const endDate = ref(today);

const formattedCost = computed(() => {
  const cost = summary.value?.total_cost_usd;
  if (cost == null) return 'N/A';
  return `$${Number(cost).toFixed(4)}`;
});

const formatNumber = value => {
  if (value == null) return '-';
  return Number(value).toLocaleString();
};

const chartData = computed(() => ({
  labels: timeseries.value.map(d => d.date),
  datasets: [
    {
      label: t('INTEGRATION_APPS.OPENAI_TOKEN_USAGE.CHART.INPUT_TOKENS'),
      data: timeseries.value.map(d => d.input_tokens),
      borderColor: 'rgb(59, 130, 246)',
      backgroundColor: 'rgba(59, 130, 246, 0.1)',
      tension: 0.3,
      fill: true,
    },
    {
      label: t('INTEGRATION_APPS.OPENAI_TOKEN_USAGE.CHART.OUTPUT_TOKENS'),
      data: timeseries.value.map(d => d.output_tokens),
      borderColor: 'rgb(34, 197, 94)',
      backgroundColor: 'rgba(34, 197, 94, 0.1)',
      tension: 0.3,
      fill: true,
    },
  ],
}));

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { position: 'bottom' },
  },
  scales: {
    x: { grid: { display: false } },
    y: { beginAtZero: true },
  },
};

const fetchData = async () => {
  isLoading.value = true;
  try {
    const [summaryRes, timeseriesRes] = await Promise.all([
      CaptainAnalyticsAPI.getTokenUsageSummary({
        startDate: startDate.value,
        endDate: endDate.value,
      }),
      CaptainAnalyticsAPI.getTokenUsageTimeseries({
        startDate: startDate.value,
        endDate: endDate.value,
      }),
    ]);
    summary.value = summaryRes.data;
    timeseries.value = timeseriesRes.data;
  } catch {
    // silent
  } finally {
    isLoading.value = false;
  }
};

watch([startDate, endDate], fetchData);
onMounted(fetchData);
</script>

<template>
  <div class="mt-6 flex flex-col gap-4">
    <h3 class="text-lg font-semibold text-n-slate-12">
      {{ $t('INTEGRATION_APPS.OPENAI_TOKEN_USAGE.TITLE') }}
    </h3>

    <div class="flex gap-3 flex-wrap items-end">
      <div class="flex flex-col gap-1">
        <label class="text-sm text-n-slate-11">
          {{ $t('INTEGRATION_APPS.OPENAI_TOKEN_USAGE.FILTERS.START_DATE') }}
        </label>
        <input
          v-model="startDate"
          type="date"
          class="px-3 py-2 rounded-lg border border-n-container bg-n-solid-2 text-n-slate-12 text-sm"
        />
      </div>
      <div class="flex flex-col gap-1">
        <label class="text-sm text-n-slate-11">
          {{ $t('INTEGRATION_APPS.OPENAI_TOKEN_USAGE.FILTERS.END_DATE') }}
        </label>
        <input
          v-model="endDate"
          type="date"
          class="px-3 py-2 rounded-lg border border-n-container bg-n-solid-2 text-sm text-n-slate-12"
        />
      </div>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
      <MetricCard
        :title="$t('INTEGRATION_APPS.OPENAI_TOKEN_USAGE.METRICS.TOTAL_TOKENS')"
        :value="summary ? formatNumber(summary.total_tokens) : '-'"
        :is-loading="isLoading"
      />
      <MetricCard
        :title="
          $t('INTEGRATION_APPS.OPENAI_TOKEN_USAGE.METRICS.OUTPUT_TOKENS')
        "
        :value="summary ? formatNumber(summary.total_output_tokens) : '-'"
        :is-loading="isLoading"
      />
      <MetricCard
        :title="
          $t('INTEGRATION_APPS.OPENAI_TOKEN_USAGE.METRICS.ESTIMATED_COST')
        "
        :value="isLoading ? '-' : formattedCost"
        :is-loading="isLoading"
      />
    </div>

    <div
      class="flex flex-col p-4 rounded-xl bg-n-solid-2 shadow outline-1 outline outline-n-container"
    >
      <h4 class="text-base font-medium text-n-slate-12 mb-4">
        {{ $t('INTEGRATION_APPS.OPENAI_TOKEN_USAGE.CHART.TITLE') }}
      </h4>
      <div v-if="isLoading" class="flex items-center justify-center h-52">
        <Spinner />
      </div>
      <div
        v-else-if="timeseries.length === 0"
        class="flex items-center justify-center h-52 text-n-slate-11"
      >
        {{ $t('INTEGRATION_APPS.OPENAI_TOKEN_USAGE.NO_DATA') }}
      </div>
      <div v-else class="h-52">
        <Line :data="chartData" :options="chartOptions" />
      </div>
    </div>

    <div
      v-if="summary?.by_model?.length"
      class="flex flex-col p-4 rounded-xl bg-n-solid-2 shadow outline-1 outline outline-n-container"
    >
      <h4 class="text-base font-medium text-n-slate-12 mb-3">
        {{ $t('INTEGRATION_APPS.OPENAI_TOKEN_USAGE.BY_MODEL.TITLE') }}
      </h4>
      <div class="overflow-x-auto">
        <table class="w-full text-sm">
          <thead>
            <tr class="text-left text-n-slate-11 border-b border-n-container">
              <th class="pb-2 font-medium">
                {{ $t('INTEGRATION_APPS.OPENAI_TOKEN_USAGE.BY_MODEL.MODEL') }}
              </th>
              <th class="pb-2 font-medium text-right">
                {{
                  $t('INTEGRATION_APPS.OPENAI_TOKEN_USAGE.BY_MODEL.INPUT_TOKENS')
                }}
              </th>
              <th class="pb-2 font-medium text-right">
                {{
                  $t(
                    'INTEGRATION_APPS.OPENAI_TOKEN_USAGE.BY_MODEL.OUTPUT_TOKENS'
                  )
                }}
              </th>
              <th class="pb-2 font-medium text-right">
                {{ $t('INTEGRATION_APPS.OPENAI_TOKEN_USAGE.BY_MODEL.COST') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in summary.by_model"
              :key="row.model"
              class="border-b border-n-container last:border-0"
            >
              <td class="py-2 text-n-slate-12">{{ row.model }}</td>
              <td class="py-2 text-right text-n-slate-12">
                {{ formatNumber(row.input_tokens) }}
              </td>
              <td class="py-2 text-right text-n-slate-12">
                {{ formatNumber(row.output_tokens) }}
              </td>
              <td class="py-2 text-right text-n-slate-12">
                ${{ Number(row.cost_usd || 0).toFixed(4) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>
